import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import { createGlobalStyle } from 'styled-components';
import { cn } from '@/lib/utils';
import { IconCheck } from '@tabler/icons-react';
import { getDropdownMenuItemBgStyle, menuItemBaseClassNames, menuItemSelectedClassNames, menuItemDisabledClassNames, menuItemHoverActiveClassNames } from '@styles/common/dropdownMenuStyles';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
export const DropdownMenuGlobalStyle = createGlobalStyle(["[data-radix-popper-content-wrapper]{z-index:50000 !important;}"]);
const DropdownMenuSubTrigger = /*#__PURE__*/React.forwardRef(({
  className,
  inset,
  children,
  ...props
}, ref) => /*#__PURE__*/_jsx(DropdownMenuPrimitive.SubTrigger, {
  ref: ref,
  className: cn('flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-neutral-100 data-[state=open]:bg-neutral-100 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 dark:focus:bg-neutral-800 dark:data-[state=open]:bg-neutral-800', inset && 'pl-8', className),
  ...props,
  children: /*#__PURE__*/_jsxs(_Fragment, {
    children: [children, /*#__PURE__*/_jsx(ChevronRight, {
      className: "ml-auto"
    })]
  })
}));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = /*#__PURE__*/React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/_jsx(DropdownMenuPrimitive.SubContent, {
  ref: ref,
  className: cn('z-50 min-w-[8rem] overflow-hidden rounded-md border border-neutral-200 bg-white p-1 text-neutral-950 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin] dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-50', className),
  ...props
}));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
const DropdownMenuContent = /*#__PURE__*/React.forwardRef(({
  className,
  sideOffset = 4,
  ...props
}, ref) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx(DropdownMenuGlobalStyle, {}), /*#__PURE__*/_jsx(DropdownMenuPrimitive.Portal, {
    children: /*#__PURE__*/_jsx(DropdownMenuPrimitive.Content, {
      ref: ref,
      sideOffset: sideOffset,
      className: cn('p-1 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md bg-white text-neutral-950 shadow-md', 'border border-solid rounded-lg border-my-gray-80', 'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95', 'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]', 'dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-50', className),
      ...props
    })
  })]
}));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
export const DropdownMenuItemContent = ({
  children,
  isSelected
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      className: "flex flex-grow min-w-[1px]",
      children: children
    }), isSelected && /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(IconCheck, {
        size: 16,
        color: "var(--blue-50)"
      })
    })]
  });
};
const DropdownMenuItem = /*#__PURE__*/React.forwardRef(({
  className,
  inset,
  selected,
  children,
  ...props
}, ref) => {
  const itemStyle = React.useMemo(() => {
    return {
      ...getDropdownMenuItemBgStyle(),
      fontSize: '0.8125rem',
      lineHeight: '1rem',
      fontWeight: '500'
    };
  }, []);
  return /*#__PURE__*/_jsx(DropdownMenuPrimitive.Item, {
    ref: ref,
    "data-selected": selected ? '' : undefined,
    style: {
      ...itemStyle
    },
    className: cn(menuItemBaseClassNames, menuItemSelectedClassNames, menuItemDisabledClassNames, menuItemHoverActiveClassNames, inset && 'pl-8', className),
    ...props,
    children: /*#__PURE__*/_jsx(DropdownMenuItemContent, {
      isSelected: selected,
      children: children
    })
  });
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuCheckboxItem = /*#__PURE__*/React.forwardRef(({
  className,
  children,
  checked,
  ...props
}, ref) => /*#__PURE__*/_jsx(DropdownMenuPrimitive.CheckboxItem, {
  ref: ref,
  className: cn('relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-neutral-100 focus:text-neutral-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-neutral-800 dark:focus:text-neutral-50', className),
  checked: checked,
  ...props,
  children: /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center",
      children: /*#__PURE__*/_jsx(DropdownMenuPrimitive.ItemIndicator, {
        children: /*#__PURE__*/_jsx(Check, {
          className: "w-4 h-4"
        })
      })
    }), children]
  })
}));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
const DropdownMenuRadioItem = /*#__PURE__*/React.forwardRef(({
  className,
  children,
  ...props
}, ref) => /*#__PURE__*/_jsx(DropdownMenuPrimitive.RadioItem, {
  ref: ref,
  className: cn('relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-neutral-100 focus:text-neutral-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-neutral-800 dark:focus:text-neutral-50', className),
  ...props,
  children: /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center",
      children: /*#__PURE__*/_jsx(DropdownMenuPrimitive.ItemIndicator, {
        children: /*#__PURE__*/_jsx(Circle, {
          className: "w-2 h-2 fill-current"
        })
      })
    }), children]
  })
}));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
const DropdownMenuLabel = /*#__PURE__*/React.forwardRef(({
  className,
  inset,
  ...props
}, ref) => /*#__PURE__*/_jsx(DropdownMenuPrimitive.Label, {
  ref: ref,
  className: cn('py-2 pl-3 !text-my-gray-50 text-label-sm-600', inset && 'pl-8', className),
  ...props
}));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = /*#__PURE__*/React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/_jsx(DropdownMenuPrimitive.Separator, {
  ref: ref,
  className: cn('-mx-1 my-1 h-px bg-my-gray-80 dark:bg-neutral-800', className),
  ...props
}));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
const DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return /*#__PURE__*/_jsx("span", {
    className: cn('ml-auto text-xs tracking-widest opacity-60', className),
    ...props
  });
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';
export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuRadioItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuGroup, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuRadioGroup };